import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { ReturningReport, InsightsRange, useShopperInsightsApi } from '../../../common/hooks/use-shopper-insights-api.hook';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { formatNumber } from '../../../components/design-system/admin/format';
import { BarList } from '../../../components/design-system/analytics/BarList';
import { MiniStat } from '../../../components/design-system/analytics/TrendChart';
import { formatPercent } from './assistant-shared';
import { formatLift, hasReturningActivity, returningLift } from './returning-visitors';

/**
 * The returning-visitor nudge on the Shopping Assistant dashboard: how often it
 * was shown, what the shoppers who saw it did against the one in ten held out
 * of it, and the products shoppers keep coming back to.
 *
 * Fetches on its own and draws nothing until there is something to say, so a
 * vendor who never switched the nudge on never sees an empty card.
 */
export const ReturningVisitorsCard = ({ range }: { range: InsightsRange }) => {
    const { t } = useTranslation();
    const api = useShopperInsightsApi();
    const [report, setReport] = useState<ReturningReport | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.getReturning(range)
            .then((r) => { if (!cancelled) setReport(r); })
            // An API without the route: the card simply stays away.
            .catch(() => { if (!cancelled) setReport(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate]);

    if (loading) return <Skeleton className="h-40 w-full rounded-xl" data-testid="returning-card-loading" />;
    if (!hasReturningActivity(report)) return null;

    const lift = returningLift(report);
    const { nudges, nudged, holdout } = report as ReturningReport;

    return (
        <SectionCard
            title={t('SHOPPER_UI.DASHBOARD.RETURNING_TITLE')}
            description={t('SHOPPER_UI.DASHBOARD.RETURNING_DESC')}
            action={(
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/assistant/settings?tab=returning">{t('SHOPPER_UI.DASHBOARD.OPEN_SETTINGS')}</Link>
                </Button>
            )}
        >
            <div className="flex flex-col gap-6" data-testid="returning-card">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                    <MiniStat
                        label={t('SHOPPER_UI.DASHBOARD.RETURNING_SHOWN')}
                        value={formatNumber(nudges.shown)}
                        sub={t('SHOPPER_UI.DASHBOARD.RETURNING_CLICK_SUB', { rate: formatPercent(nudges.clickRate) })}
                    />
                    <MiniStat
                        label={t('SHOPPER_UI.DASHBOARD.RETURNING_CART')}
                        value={formatPercent(nudged.cartRate)}
                        sub={t('SHOPPER_UI.DASHBOARD.RETURNING_CART_SUB', { count: nudged.clickedCart, devices: nudged.devices })}
                    />
                    <MiniStat
                        label={t('SHOPPER_UI.DASHBOARD.RETURNING_HOLDOUT')}
                        value={formatPercent(holdout.cartRate)}
                        sub={t('SHOPPER_UI.DASHBOARD.RETURNING_HOLDOUT_SUB', { count: holdout.clickedCart, devices: holdout.devices })}
                    />
                </div>
                <p className="text-sm" data-testid="returning-lift">
                    {lift === null
                        ? <span className="text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.RETURNING_TOO_EARLY')}</span>
                        : t('SHOPPER_UI.DASHBOARD.RETURNING_LIFT', { lift: formatLift(lift) })}
                </p>
                <div>
                    <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.RETURNING_PRODUCTS')}</p>
                    <BarList
                        emptyText={t('SHOPPER_UI.DASHBOARD.RETURNING_EMPTY')}
                        items={(report as ReturningReport).topProducts.slice(0, 6).map((p) => ({
                            key: p.sku,
                            label: p.sku,
                            value: p.returningDevices,
                        }))}
                    />
                </div>
            </div>
        </SectionCard>
    );
};
